import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, TextField } from "@mui/material";
import { createListStore } from "../../common/listStore";
import { renderListStore } from "../../common/listStoreUi";
import { renderClose } from "../../common/dialog";
import { useState } from "react";
import { CustomerDto } from "./types";
import { map } from "rxjs";
import { CustomerStateId, CustomerFlagId, EnumItemDto, useCustomerSexTypes, useCustomerStates, useCustomerFlagsEnum } from "../../common/enum";
import { chunkString, useDefaultValues } from "../../common/defaultValues";
import { KeysOfType } from "../../common/utils";
import { boolInputMarginTop } from "../../common/entityStoreForm";
import { UseEntityStore } from "../../common/entityStore";
import { customerDialogTitle } from "./common";
import { orderBy } from "lodash";
import { useConfig } from "../../common/config";
import { customerAddressHistoryChangedFieldBackgroundColor } from "../../material-ui-theme";
import { NumberField } from "../../common/form";

interface CustomerHistoryDto {
    id: number;
    title: string | null;
    fname: string | null;
    lname: string | null;
    strt1: string | null;
    strt2: string | null;
    coadr: string | null;
    zipCode: string | null;
    city: string | null;
    tel: string | null;
    mob: string | null;
    email: string | null;
    customerSexTypeId: number | null;
    dunLvl: number | null;
    customerStateId: CustomerStateId | null;
    
    emailStopId: CustomerFlagId | null;
    mailStopId: CustomerFlagId | null;
    phoneStopId: CustomerFlagId | null;
    emailStatusId: CustomerFlagId | null;
    mailStatusId: CustomerFlagId | null;
    telStatusId: CustomerFlagId | null;
    mobStatusId: CustomerFlagId | null;
    emailStopSystemId: CustomerFlagId | null;
    loserStatusId: CustomerFlagId | null;

    allowOfr: boolean | null;
    langPref: string | null;
    sc1: string | null;
    sc2: string | null;
    userName: string;
    updateTime: string;
}

interface CustomerHistory extends CustomerHistoryDto {
    changedProps: (keyof CustomerHistoryDto)[]
}

function getCustomerHistories(entries: CustomerHistoryDto[], customer: CustomerDto) {
    const ignoredProps: (keyof CustomerHistoryDto)[] = ["id", "userName", "updateTime"];
    let last: CustomerHistory = { ...customer, id: 0, updateTime: "", userName: "", changedProps: [] };
    const result: CustomerHistory[] = [];
    for (const entry of orderBy(entries, p => p.updateTime, "desc")) {
        const changedProps = Object.entries(entry).filter(p => !ignoredProps.includes(p[0] as keyof CustomerHistoryDto) && p[1] != null)
            .map(p => p[0] as keyof CustomerHistoryDto);
        const curr: CustomerHistory = {
            ...last,
            id: entry.id,
            userName: entry.userName,
            updateTime: entry.updateTime,
            changedProps,
        };
        last = { ...curr, ...Object.fromEntries(Object.entries(entry).filter(p => p[1] != null)) };
        result.push(last);
    }
    return result;
}

export function AddressHistoryDialog(props: {
    close: () => void;
    useCustomerStore: UseEntityStore<CustomerDto>;
}) {
    const customer = props.useCustomerStore(p => p.serverData);
    const [useRawCustomerHistoriesStore] = useState(() => createListStore<CustomerHistoryDto>({
        path: "customers/customerHistories/" + customer.id
    }));
    const rawCustomerHistoriesStore = useRawCustomerHistoriesStore();
    const [useCustomerHistoriesStore] = useState(() => createListStore({
        data: rawCustomerHistoriesStore.items$.pipe(map(p => ({ items: getCustomerHistories(p.items, customer), isFetching: p.isFetching }))),
        autoSelectFirstItem: true, listProps: [
            { column: "updateTime", label: "Time", width: 180, type: "datetime" },
            { column: "userName", label: "User", width: 150 },
        ]
    }));
    const selectedItem = useCustomerHistoriesStore(p => p.selectedItem);
    const sexTypes = useCustomerSexTypes(p => p.items);
    const customerStates = useCustomerStates(p => p.items);
    const customerFlags = useCustomerFlagsEnum(p => p.items);
    const def = useDefaultValues();
    const { theme } = useConfig(p => p.clientData);
    const languages: EnumItemDto[] = chunkString(def.serverData.language || "", 2).map(p => ({ id: p, name: p }));

    function isChanged(prop: keyof CustomerHistoryDto) {
        return selectedItem?.changedProps.includes(prop);
    }
    function colorAndfocused(prop: keyof CustomerHistoryDto): { color?: "negative", backgroundColor?: string; focused?: boolean } {
        return isChanged(prop) ? { color: "negative", backgroundColor: customerAddressHistoryChangedFieldBackgroundColor, focused: true } : { focused: false };
    }

    function customerEnumField(prop: KeysOfType<CustomerHistory, string | number | null>, label: string, enumItems: EnumItemDto[]) {
        const config = colorAndfocused(prop);
        return <TextField value={selectedItem ? enumItems.find(p => p.id === selectedItem[prop])?.name || "" : ""} label={label}
            InputProps={{ disabled: true }} color={config.color} focused={config.focused} style={{ backgroundColor: config.backgroundColor }} />;
    }
    function customerTextField(prop: KeysOfType<CustomerHistory, string | null>, label: string) {
        const config = colorAndfocused(prop);
        return <TextField value={selectedItem ? selectedItem[prop] || "" : ""} label={label} InputProps={{ disabled: true }}
            color={config.color} focused={config.focused} style={{ backgroundColor: config.backgroundColor }} />;
    }
    function customerBoolField(prop: KeysOfType<CustomerHistory, boolean | null>, label: string) {
        const config = colorAndfocused(prop);
        return <FormControl style={{
            backgroundColor: config.backgroundColor
        }}>
            <FormControlLabel control={<Checkbox disabled checked={selectedItem ? selectedItem[prop] || false : false} />} label={label}
                sx={{
                    ".Mui-disabled": { color: config.color ? theme.palette[config.color].main + " !important" : undefined },
                }} />
        </FormControl>;
    }
    function customerNumberField(prop: KeysOfType<CustomerHistory, number | null>, label: string) {
        const config = colorAndfocused(prop);
        return <NumberField value={selectedItem?.[prop]} label={label} disabled
            color={config.color} focused={config.focused} style={{ backgroundColor: config.backgroundColor }} />;
    }
    return <>
        <DialogTitle>{customerDialogTitle("Address history", customer)}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid container item xs={8} columnSpacing={2} alignSelf="flex-start">
                    <Grid item xs={4}><TextField value={customer.custId} label="Cust ID" InputProps={{ disabled: true }} /></Grid>
                    <Grid item xs={4}>{customerEnumField("customerSexTypeId", "Gender", sexTypes)}</Grid>
                    <Grid item xs={4}>{languages.length > 1 && customerTextField("langPref", "Language")}</Grid>

                    <Grid item xs={4}>{customerTextField("title", "Title")}</Grid>
                    <Grid item xs={4}>{customerTextField("fname", "First name")}</Grid>
                    <Grid item xs={4}>{customerTextField("lname", "Last name")}</Grid>

                    <Grid item xs={12}>{customerTextField("coadr", "C/O Address")}</Grid>

                    <Grid item xs={8}>{customerTextField("strt1", "Street")}</Grid>
                    <Grid item xs={4}>{customerTextField("strt2", "House number")}</Grid>

                    <Grid item xs={4}>{customerTextField("zipCode", "ZIP code")}</Grid>
                    <Grid item xs={8}>{customerTextField("city", "City")}</Grid>

                    <Grid item xs={4}>{customerTextField("tel", "Phone")}</Grid>
                    <Grid item xs={4}>{customerTextField("mob", "Mobile")}</Grid>
                    <Grid item xs={4} />

                    <Grid item xs={12}>{customerTextField("email", "E-mail")}</Grid>

                    <Grid item xs={3}>{customerEnumField("customerStateId", "Status", customerStates)}</Grid>
                    <Grid item xs={3} mt={boolInputMarginTop}>{customerBoolField("allowOfr", "Allow offers")}</Grid>
                    <Grid item xs={2}>{customerNumberField("dunLvl", "Dun level")}</Grid>
                    <Grid item xs={2}>{customerTextField("sc1", "SC1")}</Grid>
                    <Grid item xs={2}>{customerTextField("sc2", "SC2")}</Grid>

                    <Grid item xs={4}>{customerEnumField("mailStopId", "Mail stop", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("emailStopId", "E-mail stop", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("phoneStopId", "Phone stop", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("mailStatusId", "Mail status", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("emailStatusId", "E-mail status", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("telStatusId", "Phone status", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("mobStatusId", "Mobile status", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("emailStopSystemId", "E-mail stop system", customerFlags)}</Grid>
                    <Grid item xs={4}>{customerEnumField("loserStatusId", "Loser status", customerFlags)}</Grid>
                </Grid>
                <Grid item xs={4}>
                    {renderListStore(useCustomerHistoriesStore, { style: { minHeight: "100%" } })}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

